import { cva } from 'class-variance-authority';
import { MdCheck, MdRemove } from 'react-icons/md';
import { Checkbox as CheckboxPrimitive } from '@/components/ui/checkbox';

interface Props {
  children?: React.ReactNode;
  onChange?: (v: boolean) => void;
  value?: string | number;
  checked?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
}

const labelClass = cva('group m-0 flex items-center gap-x-xs py-[2px] font-normal select-none', {
  variants: {
    disabled: {
      true: 'cursor-not-allowed opacity-50',
      false: 'cursor-pointer',
    },
  },
});

const checkboxClass = cva(
  'box-content flex size-md items-center justify-center rounded-sm border-2 border-solid p-0 text-[length:inherit] outline-hidden transition-colors',
  {
    variants: {
      checked: {
        false: '',
        true: 'border-primary bg-primary',
      },
      indeterminate: {
        true: 'border-primary bg-primary',
        false: '',
      },
      disabled: {
        true: 'opacity-50',
        false: 'cursor-pointer',
      },
    },
    compoundVariants: [
      // 未チェックの配色は indeterminate と併存させない（bg-white が bg-primary を上書きするため）
      {
        checked: false,
        indeterminate: false,
        class: 'border-border bg-white',
      },
      {
        checked: true,
        disabled: false,
        class: 'group-hover:border-primary_darken group-hover:bg-primary_darken',
      },
      {
        checked: false,
        indeterminate: true,
        disabled: false,
        class: 'group-hover:border-primary_darken group-hover:bg-primary_darken',
      },
      {
        checked: false,
        indeterminate: false,
        disabled: false,
        class: 'group-hover:border-primary_darken group-hover:bg-white',
      },
    ],
  }
);

const iconClass = cva('text-white transition-opacity', {
  variants: {
    checked: {
      true: 'opacity-100',
      false: 'opacity-0',
    },
    indeterminate: {
      true: 'opacity-100',
      false: 'opacity-0',
    },
  },
});

export default function Checkbox({
  children,
  value,
  onChange,
  checked,
  disabled,
  indeterminate,
}: Props) {
  const radixChecked = indeterminate ? 'indeterminate' : !!checked;
  const isChecked = !!checked;
  const isIndeterminate = !!indeterminate;
  const isDisabled = !!disabled;

  return (
    <label className={labelClass({ disabled: isDisabled })}>
      <CheckboxPrimitive
        value={value}
        checked={radixChecked}
        onCheckedChange={(state) => onChange?.(state === true)}
        disabled={disabled}
        className={checkboxClass({
          checked: isChecked,
          disabled: isDisabled,
          indeterminate: isIndeterminate,
        })}
      >
        {isIndeterminate ? (
          <MdRemove className={iconClass({ indeterminate: isIndeterminate })} />
        ) : (
          <MdCheck className={iconClass({ checked: isChecked })} />
        )}
      </CheckboxPrimitive>
      {children}
    </label>
  );
}
